import 'swiper/css';

import config from '@actr/config';
import { IconChevronLeft } from '@actr/icons/chevron-left';
import { IconChevronRight } from '@actr/icons/chevron-right';
import { Sizes, ThemeColorValueImportance, ThemeColorValueTypes } from '@actr/style/src/specs/theme-v2';
import { Button, ButtonSizes, ButtonTypes } from '@actr/ui/src/components/_atoms/button';
import { FlexAlight, FlexDirection, FlexJustify, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { formatMoney } from '@actr/utilities/src/currency';
import React, { useCallback, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';

import aviaMainPageYMLogger, { AviaMainPageYMLogs } from '@/logs/ym/avia/main-page';
import type { HotOffer } from '@/services/models/avia/hot-offers';
import ChannelDecoratedLink from '@/ui/channel-decorated-link';
import { formatOfferDate } from '@/utils/get-formated-date';

import * as Styled from './style.css';

interface HotOffersBlockProps {
  offers: HotOffer[];
}

const HotOffersBlock: React.FC<HotOffersBlockProps> = ({ offers }) => {
  const refSwiper = useRef<SwiperType | null>(null);

  useEffect(() => {
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.HOT_OFFERS_BLOCK_VIEW);
  }, []);

  const handleSlideLeft = useCallback(() => {
    refSwiper.current?.slidePrev();
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.HOT_OFFERS_SCROLL);
  }, []);

  const handleSlideRight = useCallback(() => {
    refSwiper.current?.slideNext();
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.HOT_OFFERS_SCROLL);
  }, []);

  const handleOfferClick = useCallback(() => {
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.HOT_OFFER_CLICK);
  }, []);

  const handleClickShowMore = useCallback(() => {
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.HOT_OFFERS_ALL_CLICK);
  }, []);

  const renderOfferCard = useCallback((offer: HotOffer) => (
    <ChannelDecoratedLink
      className={Styled.Link}
      href={offer.search_url}
      onClick={handleOfferClick}>
      <div className={Styled.Card}>
        <Text className={Styled.DiscountPercent} font="paragraph.s.semi" colorType={ThemeColorValueTypes.CONTRAST_INVERTED}>
          -
          {offer.discount_percent}
          %
        </Text>
        <FlexWrapper alight={FlexAlight.CENTER} gap={Sizes.X2S}>
          <Text font="title.l" colorType={ThemeColorValueTypes.BRAND}>
            {formatMoney(offer.current_price)}
          </Text>
          <Text className={Styled.BasePrice} font="title.s" color={ThemeColorValueImportance.LIGHTER}>
            {formatMoney(offer.base_price)}
          </Text>
        </FlexWrapper>
        <Text className={Styled.Route} font="title.s">
          {offer.departure.name}
          {' – '}
          {offer.arrival.name}
        </Text>
        <Text font="paragraph.xs" color={ThemeColorValueImportance.LIGHTER}>
          Вылет
          {' '}
          {formatOfferDate(offer.departure.date)}
          {' '}
          в
          {' '}
          {offer.departure.time}
        </Text>
      </div>
    </ChannelDecoratedLink>
  ), [handleOfferClick]);

  return (
    <ContentBlock
      className={Styled.ContentBlock}
      style={{ backgroundImage: `url('${config.cmsMediaUrl}images/seo/hot-offers-background.png')` }}>
      <FlexWrapper gap={Sizes.X2S} justify={FlexJustify.SPACE_BETWEEN}>
        <FlexWrapper direction={FlexDirection.COL} gap={Sizes.X4S}>
          <Text tagName="h3" font="title.x2l" colorType={ThemeColorValueTypes.BRAND}>
            Горящие предложения
          </Text>
          <Text>
            Нашли билеты дешевле обычного — выбирайте подходящий вариант. Подборка регулярно обновляется
          </Text>
        </FlexWrapper>
        <FlexWrapper className={Styled.SliderButtons} gap={Sizes.X2S}>
          <Button
            className={Styled.SliderButton}
            buttonType={ButtonTypes.TERTIARY}
            size={ButtonSizes.S}
            leftIcons={<IconChevronLeft />}
            onClick={handleSlideLeft}
          />
          <Button
            className={Styled.SliderButton}
            buttonType={ButtonTypes.TERTIARY}
            size={ButtonSizes.S}
            leftIcons={<IconChevronRight />}
            onClick={handleSlideRight}
          />
        </FlexWrapper>
      </FlexWrapper>
      <Space size={Sizes.M} />
      <FlexWrapper className={Styled.MobileWrapper} gap={Sizes.XS}>
        {offers.map(offer => (
          <div key={offer.id}>
            {renderOfferCard(offer)}
          </div>
        ))}
      </FlexWrapper>
      <Swiper
        className={Styled.Slider}
        slidesPerView="auto"
        loop
        onSwiper={swiper => {
          refSwiper.current = swiper;
        }}>
        {offers.map(offer => (
          <SwiperSlide key={offer.id} className={Styled.Slide}>
            {renderOfferCard(offer)}
          </SwiperSlide>
        ))}
      </Swiper>
      <Space size={Sizes.M} />
      <a href="/hot-offers">
        <Button
          className={Styled.ShowMoreButton}
          buttonType={ButtonTypes.BRAND}
          size={ButtonSizes.M}
          onClick={handleClickShowMore}>
          Больше дешёвых билетов
        </Button>
      </a>
    </ContentBlock>
  );
};

export default HotOffersBlock;
